/**
 * Session assembly for the "Today" deck.
 *
 * The conductor (`tools/conductor.py`) commits a breadth-first batch of up to
 * 20 open questions to `tools/cache/session.json` at every nightly run. The
 * console honours that order when the file exists but always rebuilds from the
 * freshly parsed question files, so the deck is never stale: answered-elsewhere
 * questions disappear, new ones append — no refresh chicken-and-egg.
 */

import type { Question } from "./questions";
import { isSnoozed } from "./questions";
import { QUIET_MODES, effectiveMode, type NodeMode } from "./nodes";

export interface SessionFileQuestion {
  id?: string;
  path?: string;
  node?: string;
  domain?: string;
  status?: string;
  title?: string;
  links?: string[];
}

export interface SessionFile {
  generated_at?: string;
  count?: number;
  total_open?: number;
  by_domain?: Record<string, number>;
  questions?: SessionFileQuestion[];
}

/** Parse `tools/cache/session.json`; `null` when absent or malformed. */
export function parseSessionFile(text: string): SessionFile | null {
  try {
    const obj = JSON.parse(text) as SessionFile;
    return typeof obj === "object" && obj !== null ? obj : null;
  } catch {
    return null;
  }
}

/** One question per node, round-robin, oldest first (mirrors conductor). */
export function roundRobin(questions: Question[]): Question[] {
  const byNode = new Map<string, Question[]>();
  for (const q of questions) {
    const list = byNode.get(q.node) ?? [];
    list.push(q);
    byNode.set(q.node, list);
  }
  for (const list of byNode.values()) {
    list.sort((a, b) => {
      const ca = a.created || "9999";
      const cb = b.created || "9999";
      if (ca !== cb) return ca.localeCompare(cb);
      return a.id.localeCompare(b.id);
    });
  }
  const nodes = [...byNode.keys()].sort();
  const out: Question[] = [];
  let idx = 0;
  for (;;) {
    let progressed = false;
    for (const node of nodes) {
      const list = byNode.get(node) ?? [];
      if (idx < list.length) {
        out.push(list[idx]);
        progressed = true;
      }
    }
    if (!progressed) break;
    idx++;
  }
  return out;
}

export interface DeckOptions {
  /** Paths from the committed session.json (conductor order), if any. */
  sessionOrder?: string[];
  /** RAW node modes (only explicit files); effective/inherited modes are resolved here. */
  modes?: ReadonlyMap<string, NodeMode | "untouched">;
  /** Paths skipped by the founder this sitting (sessionStorage). */
  skipped?: ReadonlySet<string>;
  /** Cap on the deck length; undefined = the WHOLE stack. */
  limit?: number;
}

/** Build the deck: conductor order first, then round-robin remainder. */
export function orderDeck(questions: Question[], opts: DeckOptions = {}): Question[] {
  const usable = questions.filter((q) => {
    if (q.status !== "open") return false;
    if (isSnoozed(q)) return false;
    if (opts.skipped?.has(q.path)) return false;
    return !QUIET_MODES.has(effectiveMode(opts.modes ?? new Map(), q.node));
  });

  const byPath = new Map(usable.map((q) => [q.path, q]));
  const out: Question[] = [];
  const taken = new Set<string>();

  for (const path of opts.sessionOrder ?? []) {
    const q = byPath.get(path);
    if (q && !taken.has(q.path)) {
      out.push(q);
      taken.add(q.path);
    }
  }
  const rest = usable.filter((q) => !taken.has(q.path));
  for (const q of roundRobin(rest)) {
    out.push(q);
  }
  return opts.limit === undefined ? out : out.slice(0, opts.limit);
}

/** Filter to questions carrying ANY of the selected person tags ("" = all). */
export function filterByPersons(
  questions: Question[],
  selected: ReadonlySet<string>,
): Question[] {
  if (selected.size === 0) return questions;
  return questions.filter((q) => q.persons.some((p) => selected.has(p)));
}

// ---------------------------------------------------------------------------
// session-local skips (survive a reload, never written to the repo)
// ---------------------------------------------------------------------------

const SKIP_KEY = "psychotp.deck.skipped";

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function loadSkipped(): Set<string> {
  const raw = storage()?.getItem(SKIP_KEY);
  if (!raw) return new Set();
  try {
    const parsed = JSON.parse(raw) as unknown;
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter((v): v is string => typeof v === "string")
        : [],
    );
  } catch {
    return new Set();
  }
}

export function saveSkipped(skipped: ReadonlySet<string>): void {
  storage()?.setItem(SKIP_KEY, JSON.stringify([...skipped]));
}
